import { useEffect, useState } from "react";
import { Users, Plus, HandHeart, Check, Copy, LogOut } from "lucide-react";
import PageShell from "../components/PageShell";
import PremiumGate from "../components/PremiumGate";
import { usePremium } from "../context/PremiumContext";
import {
  getDeviceId, getDisplayName, setDisplayName, getFamilyCode, setFamilyCode,
} from "../lib/personal";
import {
  familyCreate, familyJoin, familyGet, familyAddPrayer, familyPray, familyAnswer,
} from "../api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function Family() {
  const deviceId = getDeviceId();
  const { isPremium } = usePremium();
  const [name, setName] = useState(getDisplayName());
  const [code, setCode] = useState(getFamilyCode());
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [prayerText, setPrayerText] = useState("");

  const loadGroup = (c) => {
    if (!c) return;
    setLoading(true);
    familyGet(c).then(setGroup).catch(() => { setGroup(null); setFamilyCode(""); setCode(""); }).finally(() => setLoading(false));
  };
  useEffect(() => { if (code) loadGroup(code); }, []); // eslint-disable-line

  const ensureName = () => {
    if (!name.trim()) { toast.error("Please enter your name first"); return false; }
    setDisplayName(name.trim());
    return true;
  };

  const create = async () => {
    if (!ensureName() || !groupName.trim()) return;
    const g = await familyCreate(groupName.trim(), name.trim(), deviceId);
    setGroup(g); setCode(g.code); setFamilyCode(g.code);
    toast.success(`Group created · code ${g.code}`);
  };
  const join = async () => {
    if (!ensureName() || !joinCode.trim()) return;
    try {
      const g = await familyJoin(joinCode.trim().toUpperCase(), name.trim(), deviceId);
      setGroup(g); setCode(g.code); setFamilyCode(g.code);
      toast.success(`Joined ${g.name}`);
    } catch { toast.error("Group not found — check the code"); }
  };
  const addPrayer = async () => {
    if (!prayerText.trim()) return;
    await familyAddPrayer(code, deviceId, name.trim() || "Someone", prayerText.trim());
    setPrayerText(""); loadGroup(code);
  };
  const pray = async (pid) => { await familyPray(code, pid, deviceId); loadGroup(code); };
  const answer = async (pid) => { await familyAnswer(code, pid); loadGroup(code); };
  const leave = () => { setFamilyCode(""); setCode(""); setGroup(null); };
  const copyCode = () => { navigator.clipboard.writeText(code); toast.success("Code copied"); };

  if (!isPremium) {
    return (
      <PageShell title="Family Sharing" subtitle="Pray together, wherever you are" icon={Users}
        accent={{ a: "#C48B5D", b: "#7A8450", c: "#3E5641" }} testid="family-page">
        <PremiumGate feature="Family Sharing" perks={["Private family group", "Shared prayer wall", "Pray for each other"]} />
      </PageShell>
    );
  }

  if (group) {
    return (
      <PageShell title={group.name} subtitle={`${group.members.length} members`} icon={Users}
        accent={{ a: "#C48B5D", b: "#7A8450", c: "#3E5641" }} testid="family-group">
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
          <div>
            <p className="font-ui text-xs text-muted-foreground">Invite code</p>
            <p className="font-serif-display text-2xl tracking-[0.2em] text-primary" data-testid="family-code">{group.code}</p>
          </div>
          <button onClick={copyCode} data-testid="copy-code" className="font-ui flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm transition-colors hover:bg-muted">
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
          <div className="font-ui ml-auto flex flex-wrap gap-1.5">
            {group.members.map((m, i) => (
              <span key={i} className="rounded-full bg-muted px-3 py-1 text-xs">{m.name}</span>
            ))}
          </div>
          <button onClick={leave} data-testid="leave-group" className="font-ui flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted">
            <LogOut className="h-3.5 w-3.5" /> Leave
          </button>
        </div>

        <div className="mb-6 rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
          <p className="font-ui mb-3 text-sm font-semibold">Share a prayer request</p>
          <Textarea data-testid="family-prayer-text" value={prayerText} onChange={(e) => setPrayerText(e.target.value)} placeholder="What can your family pray for?" rows={2} className="font-ui" />
          <button onClick={addPrayer} data-testid="family-add-prayer" className="font-ui mt-3 flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
            <Plus className="h-4 w-4" /> Post request
          </button>
        </div>

        <div className="space-y-3">
          {group.prayers.length === 0 && (
            <p className="font-ui text-center text-sm text-muted-foreground">No prayer requests yet — be the first to share.</p>
          )}
          {group.prayers.map((p) => (
            <div key={p.id} data-testid={`family-prayer-${p.id}`}
              className={`rounded-2xl border p-5 backdrop-blur-md ${p.answered ? "border-primary/40 bg-primary/5" : "border-border bg-background/40"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-ui text-xs font-semibold text-primary">{p.name}</p>
                  <p className="font-ui mt-1 text-sm leading-relaxed text-foreground/90">{p.text}</p>
                </div>
                {p.answered && <span className="font-ui shrink-0 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">Answered</span>}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => pray(p.id)} data-testid={`pray-${p.id}`}
                  className="font-ui flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-muted">
                  <HandHeart className="h-3.5 w-3.5 text-primary" /> Praying ({(p.prayed_by || []).length})
                </button>
                {!p.answered && (
                  <button onClick={() => answer(p.id)} data-testid={`answer-${p.id}`}
                    className="font-ui flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs transition-colors hover:bg-muted">
                    <Check className="h-3.5 w-3.5" /> Mark answered
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Family Sharing" subtitle="Pray together, wherever you are" icon={Users}
      accent={{ a: "#C48B5D", b: "#7A8450", c: "#3E5641" }} testid="family-page">
      {loading ? (
        <p className="font-ui text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
            <label className="font-ui mb-2 block text-sm font-semibold">Your name</label>
            <Input data-testid="family-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah" className="font-ui" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-border bg-background/40 p-6 backdrop-blur-md">
              <h3 className="font-serif-display text-2xl">Start a group</h3>
              <p className="font-ui mt-1 mb-4 text-sm text-muted-foreground">Create a private space for your family.</p>
              <Input data-testid="group-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Family name" className="font-ui mb-3" />
              <button onClick={create} data-testid="create-group" className="font-ui w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">
                Create group
              </button>
            </div>
            <div className="rounded-3xl border border-border bg-background/40 p-6 backdrop-blur-md">
              <h3 className="font-serif-display text-2xl">Join a group</h3>
              <p className="font-ui mt-1 mb-4 text-sm text-muted-foreground">Enter the 6-character invite code.</p>
              <Input data-testid="join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={6} className="font-ui mb-3 tracking-[0.2em]" />
              <button onClick={join} data-testid="join-group" className="font-ui w-full rounded-full border border-border py-3 text-sm font-semibold transition-colors hover:bg-muted">
                Join group
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
